"use client"

import { Region } from "@medusajs/medusa"
import { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import { SearchProduct } from "@lib/clients/nelo-api-client"
import { buildRoute } from "@lib/routes"
import { trackProductListView, trackProductSelect } from "@lib/util/ecommerce-analytics"
import { formatCurrency } from "@modules/common/utils/currency"
import { getPriceAndDiscounts } from "@modules/common/utils/productPreview"
import ProductPreviewTemplateWrapper from "@modules/products/components/ProductPreviewTemplate/wrapper"
import { ArrowButton } from "@modules/store/components/arrow-button/arrow-button"

import MarketRailComponent, { MarketRailComponentItem } from "../../../../home/components/MarketRailComponent/index"
import { MarketPlaceSection } from "../utils/structure"

export interface MarketplaceProductRailParams {
  region: Region
  section: MarketPlaceSection
  products: {
    result: SearchProduct[]
  }
  enableProductRating?: boolean
  hrefOverride?: string
  sectionIndex?: number
}

const MarketplaceProductRail = ({
  section,
  products,
  enableProductRating = false,
  hrefOverride,
  sectionIndex,
}: MarketplaceProductRailParams) => {
  const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState<boolean>(false)
  const [isRightArrowDisabled, setIsRightArrowDisabled] = useState<boolean>(false)
  const { title, hideItemsTitle, filter } = section
  const containerRef = useRef<HTMLDivElement>(null)

  const searchParams = new URLSearchParams(filter as any)
  const href = hrefOverride || `/busqueda?${searchParams.toString()}`

  const handleProductClick = (product: SearchProduct) => {
    const productIndex = products.result.findIndex((p) => p.productGroupUuid === product.productGroupUuid)
    trackProductSelect(
      product,
      productIndex,
      section.id,
      section.title?.es,
      "growthbook|marketplace_home_structure",
      window.location.pathname,
      sectionIndex
    )
  }

  const handleProductInView = (product: SearchProduct) => {
    const productIndex = products.result.findIndex((p) => p.productGroupUuid === product.productGroupUuid)
    trackProductListView(
      product,
      productIndex,
      section.id,
      section.title?.es,
      "growthbook|marketplace_home_structure",
      window.location.pathname,
      sectionIndex
    )
  }

  function onArrowClick(direction: "left" | "right") {
    if (!containerRef.current) {
      return
    }

    if (direction === "left") {
      containerRef.current.scrollBy({
        left: -containerRef.current.scrollWidth,
        behavior: "smooth",
      })
    } else {
      containerRef.current.scrollBy({
        left: containerRef.current.scrollWidth,
        behavior: "smooth",
      })
    }
  }

  useEffect(() => {
    function onScroll() {
      if (!containerRef.current) {
        return
      }

      if (containerRef.current.scrollLeft === 0) {
        setIsLeftArrowDisabled(true)
      } else {
        setIsLeftArrowDisabled(false)
      }

      if (Math.ceil(containerRef.current.scrollLeft + containerRef.current.clientWidth) >= containerRef.current.scrollWidth) {
        setIsRightArrowDisabled(true)
      } else {
        setIsRightArrowDisabled(false)
      }
    }

    containerRef.current?.addEventListener("scroll", onScroll)

    return () => {
      containerRef.current?.removeEventListener("scroll", onScroll)
    }
  }, [])

  return (
    <MarketRailComponent hideTitle={hideItemsTitle} title={title?.es} href={href} hrefLabel="Ver Todo">
      <div className="flex gap-4 overflow-x-auto no-scrollbar" ref={containerRef}>
        {products?.result?.map((product: SearchProduct) => {
          const { hasFreeShipping, originalPrice, calculatedPrice, percentageDiff } = getPriceAndDiscounts(product)
          const { countResponses, averageRating } = product
          const hasRating = !!(countResponses && averageRating)

          return (
            <div
              key={product.productGroupUuid}
              className="min-w-[130px] max-w-[130px] p-2 sm:min-w-[200px] sm:max-w-[200px] md:min-w-[260px] md:max-w-[260px]"
            >
              <MarketRailComponentItem>
                <div onClick={() => handleProductClick(product)}>
                  <ProductPreviewTemplateWrapper
                    hasFreeShipping={!!hasFreeShipping}
                    title={product.name}
                    thumbnail={product.icon.imageUrl}
                    thumbnailSize="square"
                    href={buildRoute.product(product.productUrl?.split("/")?.filter(Boolean).pop())}
                    originalPrice={`$${formatCurrency(originalPrice / 100)}`}
                    calculatedPrice={`$${formatCurrency(calculatedPrice / 100)}`}
                    percentageDiscount={percentageDiff}
                    rating={
                      enableProductRating && hasRating
                        ? {
                            count_responses: countResponses,
                            average_rating: averageRating,
                          }
                        : null
                    }
                    onInView={() => handleProductInView(product)}
                  />
                </div>
              </MarketRailComponentItem>
            </div>
          )
        })}
      </div>
      <ArrowButton
        direction="left"
        className={twMerge("arrow-breakpoint absolute inset-y-1/2 z-20 -translate-y-1/2", isLeftArrowDisabled && "opacity-50")}
        onClick={() => onArrowClick("left")}
        disabled={isLeftArrowDisabled}
      />
      <ArrowButton
        direction="right"
        className={twMerge("absolute inset-y-1/2 right-0 z-20 -translate-y-1/2 2xl:-right-8", isRightArrowDisabled && "opacity-50")}
        onClick={() => onArrowClick("right")}
        disabled={isRightArrowDisabled}
      />
    </MarketRailComponent>
  )
}

export default MarketplaceProductRail
